import { call, put, select, takeLatest } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import isFunction from 'lodash/isFunction';
import i18next from 'i18next';

import {
  accountOrganizerSlice,
  StateWithPayPalAuthorizationOrderType,
  StateWithPayPalCaptureOrderType,
  StateWithStripeClientSecretType,
} from 'src/store/organizer/slice';
import {
  AccountOrganizerContributionStartType,
  AccountOrganizerEnum,
  AccountOrganizerGetListStartType,
  AccountOrganizerPayPalContributionStartType,
  AccountOrganizerRecurringContributionStartType,
  AccountOrganizerRecurringPayPalContributionStartType,
  AccountOrganizerSaveContributionOkType,
  AccountOrganizerSaveContributionStartType,
} from 'src/models/Account/Organizer';
import { httpResponseCheckSaga } from 'src/store/commonSaga';
import {
  apiChangeAuthorizationOrder,
  apiChangePaymentMethod,
  apiCompleteCreditCardExternalContribution,
  apiCompleteTestModeCreditCardExternalContribution,
  apiCreateOrUpdateCreditCardExternalContribution,
  apiCreateOrUpdateTestModeCreditCardExternalContribution,
  apiOrganizerLoadExternalContributions,
  apiPrepareSetupIntent,
  apiSaveExternalContribution,
  apiVerifyExternalContribution,
  PrepareAuthorizationOrder,
} from 'src/api/account/organizer';
import { SafeAnyType } from 'src/utils/safeAny';
import { CommonTableResponseType } from 'src/models/Table';
import { apiValidateInternalFundingLimitReachAttempt } from 'src/api/checkout';
import { UiToast } from 'src/components/UI/toasts';
import { toggleGlobalLoader } from 'src/store/commonPart/slice';
import { CommonApiResponseType } from 'src/models/BaseApi';
import { RootState } from 'src/store/store';
import {
  AccountOrganizerContributionsChangeAuthorizationOrderRequestType,
  AccountOrganizerContributionsChangePaymentMethodRequestType,
} from 'src/models/Account/Organizer/contributions/donations';
import { apiCompleteRecovering, apiStartRecovering } from 'src/api/account/organizer/paymentRecoveryDialog';
import { Enums } from 'src/models/enums';

type ContributionPayload = AccountOrganizerSaveContributionStartType;

function* getListStart({
  payload: { type, filter, additionalParams = {} },
}: PayloadAction<AccountOrganizerGetListStartType>) {
  yield httpResponseCheckSaga(
    function* () {
      const fns: SafeAnyType = {
        [AccountOrganizerEnum.EXTERNAL_CONTRIBUTIONS]: apiOrganizerLoadExternalContributions,
      };

      const fn: SafeAnyType = fns[type];

      return isFunction(fn) ? yield call(fn, { ...filter, ...additionalParams }) : null;
    },
    function* (response) {
      if (response !== null) {
        yield put(
          accountOrganizerSlice.actions.getListOk({
            data: response as CommonTableResponseType<SafeAnyType>,
            type,
          })
        );
      }
    },
    (e) => accountOrganizerSlice.actions.getListError({ error: e, type })
  );
}

/* save a contribution */
function* saveContributionHandleError(error: CommonApiResponseType) {
  yield put(accountOrganizerSlice.actions.saveContributionError(error));
  yield put(toggleGlobalLoader(false));
}

function* saveContributionHandleSuccess(data: AccountOrganizerSaveContributionOkType['data']) {
  yield put(accountOrganizerSlice.actions.saveContributionOk({ data }));
  yield put(toggleGlobalLoader(false));
}

function* showCampaignPausedMessage() {
  UiToast.error(
    i18next.t('modals.addEditDonation.campaignPaused', { ns: 'account.organizer.externalContributions' }).toString()
  );
  yield put(accountOrganizerSlice.actions.saveContributionReset());
}

function* completeTestModeCard(payload: ContributionPayload) {
  yield httpResponseCheckSaga(
    function* () {
      return yield call(apiCompleteTestModeCreditCardExternalContribution, payload.formProps.getValue());
    },
    function* (response) {
      yield saveContributionHandleSuccess(response);
    },
    saveContributionHandleError
  );
}

function* stripeCardPaymentTestMode(payload: ContributionPayload) {
  const { formProps, stripe } = payload;

  yield httpResponseCheckSaga(
    function* () {
      return yield call(
        [stripe, stripe.handleCardPaymentForPaymentMethod],
        formProps.getValue('PaymentIntentClientSecret'),
        formProps.getValue('PaymentMethodId')
      );
    },
    function* (response: SafeAnyType) {
      if (response.error) {
        yield saveContributionHandleError(response.error.message);
      } else {
        yield completeTestModeCard(payload);
      }
    },
    saveContributionHandleError
  );
}

function* createOrUpdateTestModeCreditCardExternalContribution(payload: ContributionPayload) {
  const { formProps, resetData } = payload;

  yield httpResponseCheckSaga(
    function* () {
      return yield call(apiCreateOrUpdateTestModeCreditCardExternalContribution, formProps.getValue());
    },
    function* (response) {
      resetData(response);

      if (response.IsPayPalConfigured) {
        yield completeTestModeCard(payload);
      } else if (response.PaymentIntentClientSecret) {
        formProps.setValue('PaymentIntentClientSecret', response.PaymentIntentClientSecret);
        yield stripeCardPaymentTestMode(payload);
      } else if (response.SetupIntentClientSecret) {
        formProps.setValue('PaymentMethodId', response.PaymentMethodId);
        yield completeTestModeCard(payload);
      } else {
        yield saveContributionHandleSuccess(response);
      }
    },
    saveContributionHandleError
  );
}

function* completeCreditCardExternalContribution(data: AccountOrganizerSaveContributionOkType['data']) {
  yield httpResponseCheckSaga(
    function* () {
      return yield call(apiCompleteCreditCardExternalContribution, data);
    },
    function* (response) {
      yield saveContributionHandleSuccess(response);
    },
    saveContributionHandleError
  );
}

function* stripeCardPayment(payload: ContributionPayload, isPayment: boolean) {
  const { formProps, stripe } = payload;

  yield httpResponseCheckSaga(
    function* () {
      if (isPayment) {
        return yield call([stripe, stripe.handleCardPayment], formProps.getValue('PaymentIntentClientSecret'));
      }

      return yield call([stripe, stripe.handleCardSetup], formProps.getValue('SetupIntentClientSecret'));
    },
    function* (response: SafeAnyType) {
      if (response.error) {
        yield saveContributionHandleError(response.error.message);
      } else {
        if (!isPayment) {
          formProps.setValue('PaymentMethodId', response.setupIntent.payment_method);
        }

        yield completeCreditCardExternalContribution(formProps.getValue());
      }
    },
    saveContributionHandleError
  );
}

function* payPalCardPayment(payload: ContributionPayload) {
  const { formProps, submitPayPalPayment } = payload;

  if (submitPayPalPayment) {
    yield httpResponseCheckSaga(
      function* () {
        return yield call(submitPayPalPayment, formProps.getValue('PayPalOrderId'));
      },
      function* () {
        yield completeCreditCardExternalContribution(formProps.getValue());
      },
      saveContributionHandleError
    );
  }
}

function* createOrUpdateCreditCardExternalContribution(payload: ContributionPayload) {
  const { formProps, resetData } = payload;

  yield httpResponseCheckSaga(
    function* () {
      return yield call(apiCreateOrUpdateCreditCardExternalContribution, formProps.getValue());
    },
    function* (response) {
      resetData(response);

      if (response.PayPalOrderId) {
        formProps.setValue('PayPalOrderId', response.PayPalOrderId);
        yield payPalCardPayment(payload);
      } else if (response.PaymentIntentClientSecret) {
        formProps.setValue('PaymentIntentClientSecret', response.PaymentIntentClientSecret);
        yield stripeCardPayment(payload, true);
      } else if (response.SetupIntentClientSecret) {
        formProps.setValue('SetupIntentClientSecret', response.SetupIntentClientSecret);
        yield stripeCardPayment(payload, false);
      } else {
        yield saveContributionHandleSuccess(response);
      }
    },
    saveContributionHandleError
  );
}

function* saveCreditCardContribution(payload: ContributionPayload) {
  const { formProps } = payload;

  yield put(toggleGlobalLoader(true));

  yield httpResponseCheckSaga(
    function* () {
      return yield call(apiVerifyExternalContribution, formProps.getValue());
    },
    function* () {
      if (formProps.getValue('CardData')) {
        formProps.setValue('CardBrand', formProps.getValue('CardData.CardBrand'));
      }

      if (formProps.getValue('IsInTestMode')) {
        yield createOrUpdateTestModeCreditCardExternalContribution(payload);
      } else {
        yield createOrUpdateCreditCardExternalContribution(payload);
      }
    },
    saveContributionHandleError
  );
}

function* validateInternalFundingLimitReachAttempt(payload: ContributionPayload) {
  const { formProps } = payload;

  if (formProps.getValue('IsInternalFundingLimitEnabled') && formProps.getValue('Amount')) {
    yield httpResponseCheckSaga(
      function* () {
        return yield call(apiValidateInternalFundingLimitReachAttempt, {
          campaignId: formProps.getValue('CampaignID'),
          amount: formProps.getValue('Amount'),
        });
      },
      function* (response) {
        if (response) {
          yield saveCreditCardContribution(payload);
        } else {
          yield showCampaignPausedMessage();
        }
      },
      saveContributionHandleError
    );
  } else {
    yield saveCreditCardContribution(payload);
  }
}

function* saveContribution({ payload }: PayloadAction<ContributionPayload>) {
  const { formProps } = payload;

  if (
    formProps.getValue('PaymentTypeCode') === Enums.campaigns.PaymentTypeCode.CreditCard &&
    formProps.getValue('ContributionStatusCodeID') < Enums.contributions.StatusCode.Submitted
  ) {
    yield validateInternalFundingLimitReachAttempt(payload);
    return;
  }

  yield httpResponseCheckSaga(
    function* () {
      return yield call(apiSaveExternalContribution, formProps.getValue());
    },
    function* (response) {
      yield saveContributionHandleSuccess(response);
    },
    saveContributionHandleError
  );
}
/* end save a contribution */

/* save a recurring contribution */
function* changePaymentMethod(data: AccountOrganizerContributionsChangePaymentMethodRequestType) {
  yield httpResponseCheckSaga(
    function* () {
      return yield call(apiChangePaymentMethod, data);
    },
    function* (response) {
      yield put(accountOrganizerSlice.actions.saveRecurringContributionOk(response));
    },
    accountOrganizerSlice.actions.saveRecurringContributionError
  );
}

function* saveRecurringContributionStripeCardPayment(
  { stripe, ContributionID }: AccountOrganizerRecurringContributionStartType,
  clientSecret: string | null
): Generator {
  yield httpResponseCheckSaga(
    function* () {
      return yield call([stripe, stripe.handleCardSetup], clientSecret);
    },
    function* (response: SafeAnyType) {
      if (response.error) {
        yield accountOrganizerSlice.actions.saveRecurringContributionError(response.error);
      } else {
        yield changePaymentMethod({ ContributionID, PaymentMethodId: response.setupIntent.payment_method });
      }
    },
    accountOrganizerSlice.actions.saveRecurringContributionError
  );
}

export function* saveRecurringContribution({
  payload,
}: PayloadAction<AccountOrganizerRecurringContributionStartType>): Generator {
  const clientSecret = (yield select(
    (state: RootState) => state.accountOrganizer.saveRecurringContribution.clientSecret
  )) as StateWithStripeClientSecretType['clientSecret'];

  if (clientSecret) {
    yield saveRecurringContributionStripeCardPayment(payload, clientSecret);
  } else {
    yield httpResponseCheckSaga(
      function* () {
        return yield call(apiPrepareSetupIntent, payload.ContributionID);
      },
      function* (response) {
        yield put(accountOrganizerSlice.actions.saveRecurringContributionClientSecret(response));
        yield saveRecurringContributionStripeCardPayment(payload, response.ClientSecret);
      },
      accountOrganizerSlice.actions.saveRecurringContributionError
    );
  }
}

function* changeAuthorizationOrder(data: AccountOrganizerContributionsChangeAuthorizationOrderRequestType) {
  yield httpResponseCheckSaga(
    function* () {
      return yield call(apiChangeAuthorizationOrder, data);
    },
    function* (response) {
      yield put(accountOrganizerSlice.actions.saveRecurringPayPalContributionOk(response));
    },
    accountOrganizerSlice.actions.saveRecurringPayPalContributionError
  );
}

function* saveRecurringContributionPayPalCardPayment(
  { submitPayPalPayment, ContributionID }: AccountOrganizerRecurringPayPalContributionStartType,
  authorizationOrderId: string
): Generator {
  yield httpResponseCheckSaga(
    function* () {
      return yield call(submitPayPalPayment, authorizationOrderId);
    },
    function* (response: SafeAnyType) {
      if (response.error) {
        yield accountOrganizerSlice.actions.saveRecurringPayPalContributionError(response.error);
      } else {
        yield changeAuthorizationOrder({ ContributionID, AuthorizationOrderId: authorizationOrderId });
      }
    },
    accountOrganizerSlice.actions.saveRecurringPayPalContributionError
  );
}

export function* saveRecurringPayPalContribution({
  payload,
}: PayloadAction<AccountOrganizerRecurringPayPalContributionStartType>): Generator {
  const authorizationOrderId = (yield select(
    (state: RootState) => state.accountOrganizer.savePayPalRecurringContribution.authorizationOrderId
  )) as StateWithPayPalAuthorizationOrderType['authorizationOrderId'];

  if (authorizationOrderId) {
    yield saveRecurringContributionPayPalCardPayment(payload, authorizationOrderId);
  } else {
    yield httpResponseCheckSaga(
      function* () {
        return yield call(PrepareAuthorizationOrder, payload.ContributionID);
      },
      function* (response) {
        yield put(accountOrganizerSlice.actions.saveRecurringPayPalContributionAuthorizationOrderId(response));
        yield saveRecurringContributionPayPalCardPayment(payload, response.AuthorizationOrderId);
      },
      accountOrganizerSlice.actions.saveRecurringPayPalContributionError
    );
  }
}
/* end save a recurring contribution */

/* payment recovery */

function* paymentRecoveryStripeHandleCard(payload: AccountOrganizerContributionStartType, clientSecret: string) {
  yield httpResponseCheckSaga(
    function* () {
      if (payload.wasStripeDataEntered) {
        return yield call(
          [payload.stripe, payload.stripe.handleCardPaymentForPaymentMethod],
          clientSecret,
          payload.PaymentMethodId
        );
      }

      return yield call([payload.stripe, payload.stripe.handleCardPayment], clientSecret);
    },
    function* (response: SafeAnyType) {
      if (response.error) {
        yield put(accountOrganizerSlice.actions.paymentRecoveryError(response.error));
      } else {
        yield httpResponseCheckSaga(
          function* () {
            return yield call(apiCompleteRecovering, { PaymentID: payload.PaymentID });
          },
          function* (response) {
            yield put(accountOrganizerSlice.actions.paymentRecoveryOK(response));
          },
          accountOrganizerSlice.actions.paymentRecoveryError
        );
      }
    },
    accountOrganizerSlice.actions.paymentRecoveryError
  );
}

function* paymentRecoveryPayPalHandleCard(payload: AccountOrganizerPayPalContributionStartType, payPalOrderId: string) {
  const { submitPayPalPayment } = payload;

  if (submitPayPalPayment) {
    yield httpResponseCheckSaga(
      function* () {
        return yield call(submitPayPalPayment, payPalOrderId);
      },
      function* (response: SafeAnyType) {
        if (response.error) {
          yield put(accountOrganizerSlice.actions.paymentRecoveryPayPalError(response.error));
        } else {
          yield httpResponseCheckSaga(
            function* () {
              return yield call(apiCompleteRecovering, { PaymentID: payload.PaymentID });
            },
            function* (response) {
              yield put(accountOrganizerSlice.actions.paymentRecoveryPayPalOK(response));
            },
            accountOrganizerSlice.actions.paymentRecoveryPayPalError
          );
        }
      },
      accountOrganizerSlice.actions.paymentRecoveryPayPalError
    );
  }
}

function* paymentRecovery({ payload }: PayloadAction<AccountOrganizerContributionStartType>): Generator {
  const clientSecret = (yield select(
    (state: RootState) => state.accountOrganizer.paymentRecovery.clientSecret
  )) as StateWithStripeClientSecretType['clientSecret'];

  if (clientSecret) {
    yield paymentRecoveryStripeHandleCard(payload, clientSecret);
  } else {
    yield httpResponseCheckSaga(
      function* () {
        return yield call(apiStartRecovering, { PaymentID: payload.PaymentID });
      },
      function* (response) {
        yield put(accountOrganizerSlice.actions.paymentRecoverySecret(response));
        yield paymentRecoveryStripeHandleCard(payload, response.ClientSecret);
      },
      accountOrganizerSlice.actions.paymentRecoveryError
    );
  }
}

function* paymentPayPalRecovery({ payload }: PayloadAction<AccountOrganizerPayPalContributionStartType>): Generator {
  const payPalOrderId = (yield select(
    (state: RootState) => state.accountOrganizer.paymentPayPalRecovery.payPalOrderId
  )) as StateWithPayPalCaptureOrderType['payPalOrderId'];

  if (payPalOrderId) {
    yield paymentRecoveryPayPalHandleCard(payload, payPalOrderId);
  } else {
    yield httpResponseCheckSaga(
      function* () {
        return yield call(apiStartRecovering, { PaymentID: payload.PaymentID });
      },
      function* (response) {
        yield put(accountOrganizerSlice.actions.paymentRecoveryPayPalSecret(response));
        yield paymentRecoveryPayPalHandleCard(payload, response.PayPalOrderId);
      },
      accountOrganizerSlice.actions.paymentRecoveryError
    );
  }
}
/* end payment recovery */

export function* accountOrganizerSagas(): Generator {
  yield takeLatest(accountOrganizerSlice.actions.getListStart.type, getListStart);
  yield takeLatest(accountOrganizerSlice.actions.saveContributionStart.type, saveContribution);
  yield takeLatest(accountOrganizerSlice.actions.saveRecurringContributionStart.type, saveRecurringContribution);
  yield takeLatest(
    accountOrganizerSlice.actions.saveRecurringPayPalContributionStart.type,
    saveRecurringPayPalContribution
  );
  yield takeLatest(accountOrganizerSlice.actions.paymentRecoveryStart.type, paymentRecovery);
  yield takeLatest(accountOrganizerSlice.actions.paymentRecoveryPayPalStart.type, paymentPayPalRecovery);
}
